// TODO: Replace these
import { preferencesAtom } from 'services/preferences/atoms';
import {
  Organization,
  OrganizationConfig,
  OrganizationGroupRole,
  User
} from 'generated-graphql/types';

// ----
import { useRecoilState, useSetRecoilState } from 'recoil';
import { userAtom } from 'services/user/atoms';
import {
  useGetOrganizationConfigByOrganizationIdQuery,
  useGetOrganizationsByOrganizationGroupIdQuery,
  useGetPlanMembershipByOrganizationQuery,
  useGetStatusSetByOrganizationQuery,
  useGetTypeformRecordsByOrganizationQuery,
  useGetUserRelationshipToOrganizationQuery,
  useGetUserQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { organizationGroupOrganizationsAtom } from 'services/organizationGroupOrganizations/atom';
import { userOrganizationAtom } from 'services/userOrganization/atom';
import {
  organizationAtom,
  organizationConfigAtom
} from 'services/organization/atom';
import { planMembershipAtom } from 'services/billing/atoms';
import { statusSetAtom } from 'services/candidate/atoms';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { typeformRecordsAtom } from 'modules/portal/atoms/typeformRecords';
import {
  interviewStagesAtom,
  rejectedReasonsAtom
} from 'services/integration/atom';
import { useClearIntegration } from 'services/integration/hooks';
import { userDataErrorAtom } from 'services/auth/atoms';

export const useUserFetcher = () => {
  const setUser = useSetRecoilState(userAtom);
  const setOrganizationGroupOrganizations = useSetRecoilState(
    organizationGroupOrganizationsAtom
  );
  const setUserOrganizationState = useSetRecoilState(userOrganizationAtom);
  const [organizationState, setOrganizationState] =
    useRecoilState(organizationAtom);
  const setPlanState = useSetRecoilState(planMembershipAtom);
  const setTypeformRecordState = useSetRecoilState(typeformRecordsAtom);
  const setStatusSet = useSetRecoilState(statusSetAtom);
  const setOrganizationConfig = useSetRecoilState(organizationConfigAtom);
  // TODO: This can be removed, just use the userAtom directly
  const setPreferencesState = useSetRecoilState(preferencesAtom);
  const setRejectedReasons = useSetRecoilState(rejectedReasonsAtom);
  const setInterviewStages = useSetRecoilState(interviewStagesAtom);
  const clearIntegration = useClearIntegration();
  const setUserDataError = useSetRecoilState(userDataErrorAtom);

  // TODO: replace with use lazy query
  const { refetch: refetchUserData } = useGetUserQuery({
    skip: true
  });

  const { refetch: refetchOrganizationGroup } =
    // TODO: replace with use lazy query
    useGetOrganizationsByOrganizationGroupIdQuery({
      skip: true
    });

  const { refetch: refetchUserRelationshipToOrganization } =
    useGetUserRelationshipToOrganizationQuery({
      skip: true
    });

  const fetchUserOrganization = async (
    _userId: string,
    organizationId: string
  ) => {
    const { data } = await refetchUserRelationshipToOrganization({
      organizationId
    });
    if (data.getUserRelationshipToOrganization) {
      setUserOrganizationState(data.getUserRelationshipToOrganization);
    }
  };

  // errorPolicy 'all' on the org-scoped bootstrap queries: a partial GraphQL
  // error (e.g. one bad field written by an admin tool) must still deliver the
  // rest of the data and fire onCompleted. With the default 'none' policy the
  // whole result is discarded, these atoms stay undefined, and PrivateRoute
  // shows its loading spinner forever — a blank white page.
  useGetTypeformRecordsByOrganizationQuery({
    skip: !organizationState,
    errorPolicy: 'all',
    variables: {
      organizationId: organizationState?.id as string
    },
    onCompleted: (result) => {
      const typeformRecords = extractQueryListItems(result);
      setTypeformRecordState(typeformRecords);
    }
  });

  useGetPlanMembershipByOrganizationQuery({
    skip: !organizationState,
    errorPolicy: 'all',
    variables: {
      organizationId: organizationState?.id as string
    },
    onCompleted: (data) => {
      const planMemberships = extractQueryListItems(data);
      setPlanState(planMemberships);
    },
    onError: (error) => {
      // PrivateRoute blocks rendering until the plan atom is set, so a query
      // that dies entirely (network failure) must surface the retry screen
      // rather than an endless spinner.
      if (error.networkError) setUserDataError(true);
    }
  });

  useGetStatusSetByOrganizationQuery({
    skip: !organizationState,
    errorPolicy: 'all',
    variables: {
      organizationId: organizationState?.id as string
    },
    onCompleted: (data) => {
      const statusSet = extractQueryListItems(data);
      setStatusSet(statusSet[0]);
    }
  });

  useGetOrganizationConfigByOrganizationIdQuery({
    skip: !organizationState,
    errorPolicy: 'all',
    variables: {
      organizationId: organizationState?.id as string
    },
    onCompleted: (result) => {
      const config = result.getOrganizationConfigByOrganizationId?.items[0];
      if (!config) {
        // No config row for the selected organization. Writing undefined to
        // the atom reads as "still loading" and spins forever — surface the
        // retry screen instead.
        setUserDataError(true);
        return;
      }
      // TODO: Replace this forced cast with a proper type guard
      setOrganizationConfig(config as OrganizationConfig);
    },
    onError: (error) => {
      // Same gate as the plan query: PrivateRoute waits on the config atom.
      if (error.networkError) setUserDataError(true);
    }
  });

  const fetchUserData = async (userId: string) => {
    const { data: userData } = await refetchUserData({
      id: userId
    });

    const user = userData.getUser as User;

    setUser(user);

    if (user.preferences) {
      setPreferencesState(user.preferences);
    }

    const { data } = await refetchOrganizationGroup({
      organizationGroupId: user.organizationGroupId as string,
      limit: 100,
      filter: { isHidden: { ne: true } }
    });

    if (!data.getOrganizationsByOrganizationGroup?.items) {
      // Throw instead of silently returning: a silent return leaves the
      // stale persisted organization selected and the route guards waiting
      // forever. Throwing lets checkUser show the retry screen.
      throw new Error('Organization list failed to load');
    }

    const organizations = data.getOrganizationsByOrganizationGroup.items.filter(
      (item) => !!item
    );

    setOrganizationGroupOrganizations(organizations);

    if (organizations.length === 0) {
      // All accounts are hidden - clear organization state so the app
      // can redirect the admin to the administrator page to unhide accounts.
      setOrganizationState(null);
      return;
    }

    const currentOrganization = organizations.find(
      (org) => org.id === organizationState?.id
    );

    changeOrganization(
      userId,
      currentOrganization ?? organizations[0],
      user.organizationGroupRole
    );
  };

  const changeOrganization = async (
    userId: string,
    organization: Organization,
    organizationGroupRole: OrganizationGroupRole | undefined | null
  ) => {
    // Only a different organisation clears it: on reload the same organisation's
    // config query may already have failed and will not run again.
    if (organization.id !== organizationState?.id) {
      setUserDataError(false);
    }
    setOrganizationState(organization);
    setInterviewStages([]);
    setRejectedReasons([]);
    // Drop the previous organization's ATS answer rather than leaving it for the
    // next reader, which would otherwise report the old organization's ATS as
    // connected until the refetch lands.
    //
    // Via clearIntegration so the credential and the "resolved for organization
    // X" marker go together. Clearing only the credential left the marker
    // behind, and switching back to an organization that had already resolved
    // then opened its gate on a null credential — read by every consumer as
    // "no ATS", with no lookup left to correct it.
    clearIntegration();
    if (
      organizationGroupRole &&
      organizationGroupRole !== OrganizationGroupRole.OrganizationGroupAdmin
    ) {
      await fetchUserOrganization(userId, organization.id);
    }
  };

  const refetchUser = async (userId: string) => {
    const { data } = await refetchUserData({
      id: userId
    });

    const user = data.getUser as User;
    setUser(user);

    if (user.preferences) {
      setPreferencesState(user.preferences);
    }
  };

  const clearUserData = () => {
    setUser(null);
    // Avoid sending a stale organization-id header after sign-out.
    setOrganizationState(null);
    setPreferencesState(null);
    setOrganizationGroupOrganizations(null);
    setUserOrganizationState(null);
    setPlanState([]);
    setTypeformRecordState(null);
    setStatusSet(null);
    setOrganizationConfig(null);
  };

  return {
    fetchUserData,
    refetchUser,
    clearUserData,
    changeOrganization
  };
};
